import { useEffect } from 'react';

import type { SlackWebhookResponse } from '@/features/Setting/Integrations/integrationRedirection/slack_webhook';
import {
  connectSlackWebhook,
  getSlackWebhookItems,
} from '@/features/Setting/Integrations/providers/SlackWebhook/SlackWebhookIntegrationUtils';
import useCrossWindowState from '@/hooks/domain/useCrossWindowState';
import type { SlackNotificationT } from '@/services/integrations/slack_webhook/types';
import useIntegrationStore from '@/stores/IntegrationStore';
import { toast } from '@/stores/pure/ToastStore';

type UseSlackWebhookProps = {
  workspaceId: string;
};

type UseSlackWebhookReturn = {
  slackChannels: SlackNotificationT[];
  connectSlack: () => void;
};

/**
 * Slack webhook 연결 로직을 추출한 훅
 * - useCrossWindowState로 OAuth 리다이렉트 처리
 * - useIntegrationStore에서 채널 목록 관리
 * - 리다이렉트 성공/실패 시 채널 목록 새로고침
 */
export function useSlackWebhook({
  workspaceId,
}: UseSlackWebhookProps): UseSlackWebhookReturn {
  const slackChannels = useIntegrationStore((state) => state.slackNotifications);
  const setNotifications = useIntegrationStore(
    (state) => state.setSlackNotifications,
  );

  const [redirect, setRedirect, removeKey] =
    useCrossWindowState<SlackWebhookResponse | null>(
      'slack-webhook-redirect',
      null,
    );

  // Slack OAuth 리다이렉트 처리 및 채널 목록 로드
  useEffect(() => {
    if (!workspaceId) return;

    if (redirect?.success) {
      setRedirect(null);
      removeKey();
    } else if (redirect && redirect?.error_msg) {
      toast(redirect?.error_msg, 'fail');
      setRedirect(null);
      removeKey();
    }

    getSlackWebhookItems(workspaceId).then((notifications) => {
      setNotifications(notifications);
    });
  }, [redirect, removeKey, setRedirect, setNotifications, workspaceId]);

  // Slack 연결 핸들러
  const connectSlack = () => {
    if (!workspaceId) return;
    connectSlackWebhook(workspaceId);
  };

  return {
    slackChannels,
    connectSlack,
  };
}
